import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy,
  OnChanges,
  inject,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';

// Constants
import {
  ADD_MESSAGE_ACTION,
  CLEAR_ACTION,
  SEARCH_ACTION,
  SHOW_ACTION,
  VIEW_MESSAGE_ACTION,
} from '../../../core/constants/statement-messaging.constant';

// Components
import { NavOverflowData, NavOverflowItem } from '@vg-constellation/angular-19/nav-overflow';

// Services
import {
  AuthorizationService,
  UserPermissions,
} from '../../../services/read-write-role/authorization.service';

// Models
import {
  StatementMessageModel,
} from '../../../core/interfaces/statement-messaging-run.model';

@Component({
  selector: 'app-message-run-detail-navigation-overflow-menu',
  templateUrl: './message-run-detail-navigation-overflow-menu.component.html',
  styleUrl: './message-run-detail-navigation-overflow-menu.component.scss',
  standalone: false,
})
export class MessageRunDetailNavigationOverflowMenuComponent
  implements OnInit, OnDestroy, OnChanges
{
  // ============================================
  // Services
  // ============================================
  private readonly authService = inject(AuthorizationService);
  private readonly destroy$ = new Subject<void>();

  // ============================================
  // Inputs/Outputs
  // ============================================
  @Output() menuItemClicked = new EventEmitter<NavOverflowItem>();
  @Input() selectedMessageDetails: StatementMessageModel | null = null;

  // ============================================
  // State
  // ============================================
  menuData: NavOverflowData = { groups: [{ items: [] }] };
  menuDataForMediumScreen: NavOverflowData = { groups: [{ items: [] }] };

  private permissions: UserPermissions = {
    canView: false,
    canEdit: false,
    canCreate: false,
    canDelete: false,
    isReadOnly: true,
  };

  // ============================================
  // Lifecycle Hooks
  // ============================================

  ngOnInit(): void {
    this.subscribeToPermissions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * React to changes in input properties
   */
  ngOnChanges(): void {
    this.updateMenuItems();
  }

  // ============================================
  // Subscriptions
  // ============================================

  /**
   * Subscribe to permission changes and update menu
   */
  private subscribeToPermissions(): void {
    this.authService.permissions$
      .pipe(takeUntil(this.destroy$))
      .subscribe((permissions) => {
        this.permissions = permissions;
        this.updateMenuItems();
      });
  }

  // ============================================
  // Menu Building
  // ============================================

  /**
   * Update menu items based on permissions
   */
  private updateMenuItems(): void {
    this.menuData = {
      groups: [{ items: this.getMenuItemsForSmallScreen() }],
    };

    this.menuDataForMediumScreen = {
      groups: [{ items: this.getMenuItemsForMediumScreen() }],
    };
  }

  /**
   * Get menu items for small screen (all items)
   */
  private getMenuItemsForSmallScreen(): NavOverflowItem[] {
    const items: NavOverflowItem[] = [];

    if (this.permissions.canView) {
      this.addBasicViewActions(items);
      this.addViewMessageAction(items);
    }

    if (this.permissions.canCreate) {
      this.addCreateMessageAction(items);
    }

    return items;
  }

  /**
   * Get menu items for medium screen (overflow only)
   */
  private getMenuItemsForMediumScreen(): NavOverflowItem[] {
    const items: NavOverflowItem[] = [];

    if (this.permissions.canView) {
      this.addViewMessageAction(items);
    }

    if (this.permissions.canCreate) {
      this.addCreateMessageAction(items);
    }

    return items;
  }

  // ============================================
  // Menu Item Builders
  // ============================================

  /**
   * Add basic view actions (search, show, clear)
   */
  private addBasicViewActions(items: NavOverflowItem[]): void {
    items.push(SEARCH_ACTION, SHOW_ACTION, CLEAR_ACTION);
  }

  /**
   * Add view message action if message is selected
   */
  private addViewMessageAction(items: NavOverflowItem[]): void {
    if (this.selectedMessageDetails) {
      items.push(VIEW_MESSAGE_ACTION);
    }
  }

  /**
   * Add create message action
   */
  private addCreateMessageAction(items: NavOverflowItem[]): void {
    items.push(ADD_MESSAGE_ACTION);
  }

  // ============================================
  // Event Handlers
  // ============================================

  /**
   * Handle menu item selection
   */
  onMessageNavigationMenu(item: NavOverflowItem): void {
    this.menuItemClicked.emit(item);
  }
}
